import React, { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import moment from 'moment'
// evertel
import { Card, CardBody, ContentPlaceholder, Row, Text } from '@evertel/web/ui'
import { DocumentViewController, DocumentTileStore, DOCUMENT_NO_ACCESS_TEXT } from '@evertel/document'
import { SessionService } from '@evertel/session/SessionService'
import { DocumentTileBody } from './DocumentTileBody'
import { useService } from '@evertel/di'
import { EventMessageBody } from '@evertel/web/message'

interface TileProps {
    documentId: number
    onClick?: (documentId: number | undefined) => void
    /**
     * When the thing showing this tile happened, e.g. the share or update message's createdDate.
     * A cached tile fetched before then is out of date for it and is refetched
     */
    since?: moment.MomentInput
    className?: string
}

const DocumentTile: React.FC<TileProps> = observer(({
    documentId,
    onClick,
    since,
    className
}) => {

    const documentViewController = useService(DocumentViewController, [documentId])
    const documentTileStore = useService(DocumentTileStore)
    const sessionService = useService(SessionService)

    useEffect(() => {
        if (!documentId) return

        documentViewController.loadTile(documentId, { since })
            .catch((error) => {
                // no-access is handled by the controller; anything else leaves the skeleton up
                // and retries on the next mount
                console.error('DocumentTile: failed to load tile', documentId, error)
            })
    }, [documentId, since])

    // read straight from the store during render, not through the controller's documentId
    // (which loadTile only sets after the first paint), so a cached tile renders immediately
    const tile = documentTileStore.findById(documentId)

    if (tile?.noAccess) {
        return (
            <EventMessageBody message={{text: DOCUMENT_NO_ACCESS_TEXT, meta: {eventType: 'document_removed'}}}/>
        )
    }

    //used in place of isLoading
    if (!tile) {
        return (
            <Card
                className={classNames('document-tile', className)}
                style={{
                    borderLeftColor: '#999',
                    borderLeftWidth: 5
                }}>
                <CardBody>
                    <ContentPlaceholder
                        width="70%"
                        color="gray200"
                        className="mt-1"
                    />
                    <Row className="mt-2">
                        <ContentPlaceholder
                            width={20}
                            height={26}
                            color="gray200"
                            className="me-2"
                        />
                        <ContentPlaceholder
                            width="40%"
                            color="gray200"
                            className="mt-1"
                        />
                    </Row>
                    {/* the footer's owner and date, as on mobile */}
                    <Row className="mt-3">
                        <ContentPlaceholder
                            width="55%"
                            color="gray200"
                            className="me-2"
                        />
                        <ContentPlaceholder
                            width="35%"
                            color="gray200"
                        />
                    </Row>
                </CardBody>
            </Card>
        )
    }

    // same wording as mobile: your own documents read as 'Me', one with no owner as blank (the
    // footer shows that as no owner)
    const ownerName = (tile.owner?.id && tile.owner.id === sessionService.currentUserId)
        ? 'Me'
        : (tile.owner?.name ?? '')

    return (
        <DocumentTileBody
            name={tile.name}
            schema={tile.schema}
            departmentShortName={tile.departmentShortName}
            fields={tile.fields}
            isArchived={tile.isArchived}
            ownerName={ownerName}
            updatedDate={tile.updatedDate}
            onClick={() => onClick?.(documentId)}
            className={className}
        />
    )
})

export { DocumentTile }
